import { useState } from "react";
import { Gift } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BonusSimulator() {
  const [bonus, setBonus] = useState("100");
  const [rollover, setRollover] = useState("3");
  const [minOdd, setMinOdd] = useState("1.50");

  const b = parseFloat(String(bonus).replace(",", "."));
  const r = parseFloat(String(rollover).replace(",", "."));
  const valid = !isNaN(b) && b > 0 && !isNaN(r) && r > 0;
  const total = valid ? b * r : null;

  return (
    <div
      data-testid="bonus-simulator"
      className="my-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-[#0B1F3A]">
        <Gift className="h-5 w-5 text-[#2BB08A]" aria-hidden="true" />
        Simulateur de bonus (rollover)
      </h3>
      <p className="mb-5 text-sm text-slate-500">
        Estimez le montant total à miser avant de pouvoir retirer les gains liés à un bonus.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="bonus-amount" className="mb-1 block text-sm font-medium text-slate-700">
            Montant du bonus (€)
          </label>
          <input
            id="bonus-amount"
            data-testid="bonus-input-amount"
            type="number"
            min="0"
            step="5"
            inputMode="decimal"
            value={bonus}
            onChange={(e) => setBonus(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
        <div>
          <label htmlFor="bonus-rollover" className="mb-1 block text-sm font-medium text-slate-700">
            Multiplicateur de mise (×)
          </label>
          <input
            id="bonus-rollover"
            data-testid="bonus-input-rollover"
            type="number"
            min="1"
            step="1"
            inputMode="decimal"
            value={rollover}
            onChange={(e) => setRollover(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
        <div>
          <label htmlFor="bonus-minodd" className="mb-1 block text-sm font-medium text-slate-700">
            Cote minimale exigée
          </label>
          <input
            id="bonus-minodd"
            data-testid="bonus-input-minodd"
            type="number"
            min="1"
            step="0.1"
            inputMode="decimal"
            value={minOdd}
            onChange={(e) => setMinOdd(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
      </div>

      {valid && (
        <div data-testid="bonus-result" className="mt-6 rounded-lg bg-[#E8F8F3] p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#064E3B]">
            Montant total à miser avant retrait
          </p>
          <p className="mt-1 font-display text-3xl font-semibold text-[#064E3B]">
            {fmt(total)} €
          </p>
          <p className="mt-1 text-sm text-[#064E3B]/80">
            {fmt(b)} € de bonus × {fmt(r)} de multiplicateur, sur des paris à cote minimale de{" "}
            {minOdd}.
          </p>
        </div>
      )}

      <div className="mt-5 rounded-lg bg-[#F3F5F8] p-4 text-sm leading-relaxed text-slate-600">
        <p className="mb-1 font-display font-semibold text-[#0B1F3A]">
          Qu'est-ce que le rollover ?
        </p>
        <p>
          Le rollover (ou multiplicateur de mise) est le nombre de fois qu'il faut miser un
          montant avant de pouvoir retirer les gains liés à un bonus. Un bonus de 100 € avec un
          rollover ×3 impose donc de miser 300 € au total, souvent sur des paris respectant une
          cote minimale. Tant que ces conditions ne sont pas remplies, les gains restent bloqués.
          Un bonus n'est donc jamais de l'argent « gratuit ».
        </p>
      </div>
    </div>
  );
}
